import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { getStoreAvailability, hasBranchStock, parseVariantIdList } from '@theme/store-availability';

/**
 * @typedef {import('@theme/events').VariantUpdateEvent} VariantUpdateEvent
 */

/**
 * Displays a product's delivery time.
 *
 * The initial/fallback text (from the brand's `custom.marke` → `lieferzeit` field) is rendered
 * server-side by the block, since it's product-level and doesn't need a live query.
 * This component only has to:
 *  1. Check live per-location stock at Aarburg/Wallisellen (via the shared
 *     "@theme/store-availability" cache, so it doesn't duplicate the fetch already
 *     made by the "Lagerbestand Filialen" block on the same page) and upgrade the
 *     text to the branch-stock wording when there's stock.
 *  2. Hide/show itself as the shopper switches variant, since purchasability
 *     (`available`) is a variant-level state.
 *
 * Precedence: physical branch stock always wins and reveals the branch-stock text,
 * regardless of `available` - Shopify's aggregate inventory_quantity can under-count
 * pickup-only locations like Aarburg. Without branch stock, the fallback (metafield)
 * text only shows while the variant is purchasable (`available` - true whether it has
 * stock elsewhere, or "Continue selling when out of stock" is on); otherwise the item
 * is simply sold out and nothing is shown.
 *
 * Variants listed in data-restricted-variants (all variants of a product with the
 * product metafield custom.externes_lager_ignorieren = true) never use the fallback
 * path: they may only be sold from branch stock, so without it they count as sold out
 * (see branch-stock-gate.js, which disables the buy buttons for them).
 *
 * @extends {Component<{text?: HTMLElement}>}
 */
class ProductDeliveryTime extends Component {
  connectedCallback() {
    super.connectedCallback();

    // The fallback text (product-level, so it stays valid across a variant switch) is
    // rendered as data-fallback-text. Older markup without it: remember the
    // server-rendered text before anything might overwrite it.
    if (this.refs.text && this.dataset.fallbackText === undefined) {
      this.dataset.fallbackText = this.refs.text.textContent ?? '';
    }

    this.#checkBranchStock(this.dataset.variantId);

    // Matches product-sku.js's ancestor selector (confirmed reliable for this shop's
    // markup) rather than the more common `.shopify-section, dialog` used elsewhere,
    // which doesn't reliably deliver the variant:update event here.
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    target?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  /**
   * @param {Event} event - Actually a VariantUpdateEvent; typed as the plain Event
   *   addEventListener expects, since VariantUpdateEvent extends Event (not
   *   CustomEvent) and only structurally adds `.detail` - don't guard on
   *   `instanceof CustomEvent`, it's always false for these.
   */
  #handleVariantUpdate = (event) => {
    const newVariant = /** @type {VariantUpdateEvent} */ (event).detail?.resource;
    if (!newVariant || String(newVariant.id) === this.dataset.variantId) return;

    this.dataset.variantId = String(newVariant.id);
    const isAvailable = Boolean(newVariant.available);
    this.dataset.available = String(isAvailable);

    // Show a tentative state immediately (fallback text, or hidden if unavailable/no
    // fallback) while the live per-location check for the new variant resolves.
    // `available` only governs this fallback path below - physical branch stock
    // (checked regardless of it) always wins if it's found, since Shopify's aggregate
    // inventory_quantity may not reflect pickup-only locations like Aarburg accurately.
    const showFallback = isAvailable && !this.#isRestricted(String(newVariant.id));
    const textElement = this.refs.text;
    if (textElement) {
      textElement.textContent = showFallback ? this.dataset.fallbackText ?? '' : '';
    }
    this.hidden = !showFallback || !this.dataset.fallbackText;

    this.#checkBranchStock(newVariant.id);
  };

  /**
   * Whether the variant may only be sold from branch stock (product metafield custom.externes_lager_ignorieren).
   * @param {string} variantId
   */
  #isRestricted(variantId) {
    return parseVariantIdList(this.dataset.restrictedVariants).has(variantId);
  }

  /**
   * @param {string} [variantId]
   */
  async #checkBranchStock(variantId) {
    const textElement = this.refs.text;
    if (!textElement) return;

    const shopDomain = this.dataset.shopDomain;
    const storefrontToken = this.dataset.storefrontToken;
    if (!variantId || !shopDomain || !storefrontToken) return;

    try {
      const locations = await getStoreAvailability(variantId, { shopDomain, storefrontToken });
      if (String(variantId) !== this.dataset.variantId) return; // a newer variant switch beat us here

      if (hasBranchStock(locations)) {
        // Physical branch stock always wins, regardless of `available`.
        textElement.textContent = this.dataset.branchText ?? '';
        this.hidden = false;
      } else if (this.dataset.available === 'true' && !this.#isRestricted(String(variantId))) {
        textElement.textContent = this.dataset.fallbackText ?? '';
        this.hidden = !this.dataset.fallbackText;
      } else {
        textElement.textContent = '';
        this.hidden = true;
      }
    } catch {
      // Live check failed (e.g. token/network issue) - keep showing the fallback text as-is.
    }
  }
}

if (!customElements.get('product-delivery-time')) {
  customElements.define('product-delivery-time', ProductDeliveryTime);
}
